import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { logoutUser } from '@/entities/user'
import { fetchUserInfo } from '@/entities/user/thunks/fetchUserInfo'
import { refreshUserToken } from '@/entities/user/thunks/refreshUserToken'
import { UserInfoDto } from '@/shared/apis/fastmile/models'
import { Button, Modal } from '@/shared/components'
import { isTokenExpired } from '@/shared/lib/JWTTokenHelpers'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { Alert, styled } from '@mui/material'

export type ProtectedRouteProps = {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const session = useAppSelector(state => state.session)
  const [isLoading, setIsLoading] = useState(true)
  const [userInfo, setUserInfo] = useState<UserInfoDto>()
  const [isSuccess, setIsSuccess] = useState(false)
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false)

  const location = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Main authentication flow
  useEffect(() => {
    const handleAuthentication = async () => {
      setIsLoading(true)

      // Check if refresh token is expired
      const isRefreshTokenExpired = isTokenExpired(session.refreshToken) || session.token === null

      if (isRefreshTokenExpired) {
        setIsLoading(false)

        return
      }

      // Check if access token is expired
      const isAccessTokenExpired = isTokenExpired(session.accessToken)

      if (isAccessTokenExpired && !isTokenRefreshing) {
        setIsTokenRefreshing(true)
        await dispatch(refreshUserToken()).unwrap()
        setIsTokenRefreshing(false)

        return
      }

      if (!isAccessTokenExpired || !isTokenRefreshing) {
        try {
          const userInformation = await dispatch(fetchUserInfo()).unwrap()

          setUserInfo(userInformation)
          setIsSuccess(true)
        } catch (error) {
          setIsSuccess(false)
        } finally {
          setIsLoading(false)
        }
      }
    }

    handleAuthentication()
  }, [dispatch, session.accessToken, session.refreshToken, session.token, isTokenRefreshing])

  // Navigation logic
  useEffect(() => {
    const isRefreshTokenExpired = isTokenExpired(session.refreshToken) || session.token === null

    if (!isLoading && !isRefreshTokenExpired) {
      if (!session.isEmailVerified) {
        navigate('/verify-email', { replace: true })
      } else if (userInfo && !userInfo.client.isOnboarded && session.isEmailVerified) {
        navigate('/onboarding/', { replace: true })
      }
    }
  }, [session, navigate, isLoading, userInfo])

  const logout = () => {
    dispatch(logoutUser()).then(() => {
      navigate('/', {
        state: { from: location },
      })
    })
  }

  // Show session expired modal if refresh token is expired
  const isRefreshTokenExpired = isTokenExpired(session.refreshToken) || session.token === null
  const shouldShowSessionExpiredModal = ((!isLoading && !isSuccess) || isRefreshTokenExpired) && !isTokenRefreshing

  return (
    <>
      {!isRefreshTokenExpired && !isLoading && children}
      {shouldShowSessionExpiredModal && (
        <Modal
          open={true}
          portal="#session-expired-dialog"
          lock={true}
          title={'Your session has expired'}
        >
          <SyledModalContent>
            <Alert severity={'info'} icon={false}>
              Sign in again to continue using the application.
            </Alert>
            <StyledButton
              primary
              fullWidth
              onClick={logout}
            >
              Sign back in
            </StyledButton>
          </SyledModalContent>
        </Modal>
      )}
    </>
  )
}

const SyledModalContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(360)};
  }
`

const StyledButton = styled(Button)`
  margin-top: ${(props) => props.theme.typography.pxToRem(20)};
`